import { ErrorResponse } from '@/utils/axios.http';
import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useEffect, useRef } from 'react';
import { FieldValues } from 'react-hook-form';

export interface StoreOnDragAndDropProps<TSchema extends FieldValues> {
  rowData: TSchema[] | string[];
  data: TSchema;
  mutate: UseMutateFunction<
    AxiosResponse<TSchema, TSchema>,
    ErrorResponse,
    Partial<TSchema>,
    unknown
  >;
  fieldName: string;
  updateIndividualItems?: boolean;
  extraFields?: Record<string, unknown>; // Add this for extra fields to include
}

export const useStoreDataOnDragAndDrop = <TSchema extends FieldValues>({
  rowData,
  data,
  mutate,
  fieldName,
  updateIndividualItems = false,
  extraFields = {},
}: StoreOnDragAndDropProps<TSchema>) => {
  const initialData = useRef<string | null>(null);
  const lastProcessedData = useRef<string | null>(null);
  const hasDropOccurred = useRef(false);

  // Setup drop event listeners and capture initial data
  useEffect(() => {
    // Store initial data on mount
    if (rowData && !initialData.current) {
      initialData.current = JSON.stringify(rowData);
      lastProcessedData.current = initialData.current;
    }

    // Track drop events
    const onDrop = () => (hasDropOccurred.current = true);

    document.addEventListener('drop', onDrop);
    document.addEventListener('dragend', onDrop);

    return () => {
      document.removeEventListener('drop', onDrop);
      document.removeEventListener('dragend', onDrop);
    };
  }, [rowData]);

  // Handle data changes
  useEffect(() => {
    // Quick validation
    if (!rowData || !data?.id) {
      /**
       * Prevent next code from running
       * by returning an empty cleanup function
       */
      return () => {};
    }

    const currentData = JSON.stringify(rowData);

    // Skip if data unchanged or matches initial data
    if (
      currentData === lastProcessedData.current ||
      currentData === initialData.current
    ) {
      /**
       * Prevent next code from running
       * by returning an empty cleanup function
       */
      return () => {};
    }

    // Always update last processed data
    lastProcessedData.current = currentData;

    // Only mutate if a drop event has occurred
    if (hasDropOccurred.current) {
      const timer = setTimeout(() => {
        if (
          updateIndividualItems &&
          Array.isArray(rowData) &&
          rowData.length > 0 &&
          typeof rowData[0] === 'object'
        ) {
          // For updating individual items with their new indexes
          // This approach updates each item individually if needed
          (rowData as TSchema[]).forEach((item, index) => {
            // Always update the index for each item since drag and drop changed the order
            if (item.id) {
              mutate({
                id: item.id,
                // ...item,
                index,
                ...extraFields,
              } as unknown as Partial<TSchema>);
            }
          });
        } else {
          // Default behavior: update the parent entity with the entire array
          mutate({
            id: data.id,
            [fieldName]: rowData as unknown as TSchema[] | string[],
          } as unknown as Partial<TSchema>);
        }

        // Reset the drop flag after processing
        hasDropOccurred.current = false;
      }, 300);

      return () => clearTimeout(timer);
    }

    // Default cleanup function
    return () => {};

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData, data?.id, updateIndividualItems]);
};
